import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import MerchantHeader from '../components/MerchantHeader';
import ShareReportModal from '../components/ShareReportModal';
import PolicyStatusWidget from '../components/PolicyStatusWidget';
import PoliciesQuickStartModal from '../components/PoliciesQuickStartModal';
import { getPoliciesStatus } from '../api/policies';
import apiClient from '../services/api';
import { toast } from 'sonner';
import { SlackApprovalStatus } from '../components/SlackApprovalStatus';
import { Tooltips } from '../copy/tooltips';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { 
  AlertTriangle, 
  TrendingUp, 
  TrendingDown, 
  Target, 
  ShieldCheck, 
  Lightbulb,
  RefreshCw,
  ChevronRight,
  ChevronDown,
  Package,
  XCircle,
  Clock,
  Repeat,
  DollarSign,
  Users,
  BarChart3,
  Search,
  CheckSquare,
  Send,
  Settings,
  Rocket,
  Share2,
  Slack
} from 'lucide-react';
import {
  DisappointmentTrendChart,
  DriverDistributionChart,
  BenchmarkComparisonChart,
  RevenueImpactChart
} from '../components/charts/TrendCharts';

// Brand ID for demo - in production this would come from user context
const DEFAULT_BRAND_ID = 'demo-brand';

// Disappointment type icons and labels
const DISAPPOINTMENT_ICONS = {
  cancellation: { icon: XCircle, label: 'Cancellations', color: 'text-red-400' },
  promise_break: { icon: AlertTriangle, label: 'Promise Breaks', color: 'text-orange-400' },
  backorder_after_purchase: { icon: Package, label: 'Backorders', color: 'text-yellow-400' },
  late_partial: { icon: Clock, label: 'Late/Partial', color: 'text-amber-400' },
  substitution_refused: { icon: Repeat, label: 'Refused Substitutions', color: 'text-purple-400' }
};

// Score color helper
const getScoreColor = (score) => {
  if (score >= 90) return 'text-emerald-400';
  if (score >= 70) return 'text-yellow-400';
  if (score >= 50) return 'text-orange-400';
  return 'text-red-400';
};

const getScoreBgColor = (score) => {
  if (score >= 90) return 'bg-emerald-500/10 border-emerald-500/20';
  if (score >= 70) return 'bg-yellow-500/10 border-yellow-500/20';
  if (score >= 50) return 'bg-orange-500/10 border-orange-500/20';
  return 'bg-red-500/10 border-red-500/20';
};

// Score Ring Component
const ScoreRing = ({ score, size = 'lg' }) => {
  const radius = size === 'lg' ? 70 : 40;
  const stroke = size === 'lg' ? 8 : 5;
  const normalizedRadius = radius - stroke * 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const strokeDashoffset = circumference - (score / 100) * circumference;
  
  return (
    <div className="relative inline-flex items-center justify-center">
      <svg 
        height={radius * 2} 
        width={radius * 2}
        className="transform -rotate-90"
      >
        <circle
          stroke="currentColor"
          fill="transparent"
          strokeWidth={stroke}
          r={normalizedRadius}
          cx={radius}
          cy={radius}
          className="text-muted/30"
        />
        <circle
          stroke="currentColor"
          fill="transparent"
          strokeWidth={stroke}
          strokeDasharray={circumference + ' ' + circumference}
          style={{ strokeDashoffset }}
          strokeLinecap="round"
          r={normalizedRadius}
          cx={radius}
          cy={radius}
          className={`${getScoreColor(score)} transition-all duration-700 ease-out`}
        />
      </svg>
      <div className="absolute flex flex-col items-center">
        <span className={`${size === 'lg' ? 'text-4xl' : 'text-xl'} font-bold ${getScoreColor(score)}`}>
          {score}
        </span>
        <span className="text-xs text-muted-foreground">/ 100</span>
      </div>
    </div>
  );
};

// Driver Card Component
const DriverCard = ({ driver, totalPromises }) => {
  const typeInfo = DISAPPOINTMENT_ICONS[driver.type] || { 
    icon: AlertTriangle, 
    label: driver.type, 
    color: 'text-gray-400' 
  };
  const Icon = typeInfo.icon;
  
  // Get tooltip based on driver type
  const getDriverTooltip = (type) => {
    switch(type) {
      case 'cancellation':
        return Tooltips.driverCancellations;
      case 'backorder_after_purchase':
        return Tooltips.driverBackorders;
      case 'promise_break':
        return Tooltips.driverPromiseBreaks;
      case 'late_partial':
        return Tooltips.driverLatePartial;
      default:
        return { title: typeInfo.label, body: "Promise reliability event that impacts customer experience." };
    }
  };
  
  const tooltip = getDriverTooltip(driver.type);
  
  return (
    <Card className="bg-card border-border hover:border-primary/30 transition-all">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`p-2 rounded-lg bg-muted ${typeInfo.color}`}>
              <Icon className="h-4 w-4" />
            </div>
            <div>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <p className="font-medium text-sm text-foreground cursor-help">{typeInfo.label}</p>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <div>
                      <p className="font-semibold">{tooltip.title}</p>
                      <p className="text-sm mt-1">{tooltip.body}</p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <p className="text-xs text-muted-foreground">{driver.count} events</p>
            </div>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="outline" className={`${typeInfo.color} border-current/30 cursor-help`}>
                  {driver.rate_per_100_promises}%
                </Badge>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <div>
                  <p className="font-semibold">{Tooltips.ratePer100.title}</p>
                  <p className="text-sm mt-1">{Tooltips.ratePer100.body}</p>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        
        <div className="space-y-2">
          <div className="flex justify-between text-xs">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-muted-foreground cursor-help">Weighted Impact</span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <div>
                    <p className="font-semibold">{Tooltips.weightedImpact.title}</p>
                    <p className="text-sm mt-1">{Tooltips.weightedImpact.body}</p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <span className="font-medium">{driver.weighted}</span>
          </div>
          <Progress 
            value={(driver.weighted / (totalPromises * 0.3)) * 100} 
            className="h-1.5"
          />
          <div className="flex gap-2 text-xs">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-red-400 cursor-help">High: {driver.severity_breakdown?.high || 0}</span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <div>
                    <p className="font-semibold">{Tooltips.severityHigh.title}</p>
                    <p className="text-sm mt-1">{Tooltips.severityHigh.body}</p>
                  </div>
                </TooltipContent>
              </Tooltip>
              
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-yellow-400 cursor-help">Med: {driver.severity_breakdown?.med || 0}</span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <div>
                    <p className="font-semibold">{Tooltips.severityMed.title}</p>
                    <p className="text-sm mt-1">{Tooltips.severityMed.body}</p>
                  </div>
                </TooltipContent>
              </Tooltip>
              
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-green-400 cursor-help">Low: {driver.severity_breakdown?.low || 0}</span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <div>
                    <p className="font-semibold">{Tooltips.severityLow.title}</p>
                    <p className="text-sm mt-1">{Tooltips.severityLow.body}</p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Playbook Card Component
const PlaybookCard = ({ recommendation }) => {
  const typeInfo = DISAPPOINTMENT_ICONS[recommendation.driver] || { 
    icon: Lightbulb, 
    label: recommendation.driver, 
    color: 'text-primary' 
  };
  const Icon = typeInfo.icon;
  
  return (
    <Card className="bg-card border-border">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <div className={`p-2 rounded-lg bg-primary/10 ${typeInfo.color}`}>
            <Icon className="h-4 w-4" />
          </div>
          <div>
            <CardTitle className="text-sm">{typeInfo.label}</CardTitle>
            <CardDescription className="text-xs">
              {recommendation.count} events • Impact: {recommendation.weighted_impact}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <ul className="space-y-2">
          {recommendation.playbooks?.map((playbook, idx) => (
            <li key={idx} className="flex items-start gap-2 text-sm">
              <ChevronRight className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
              <span className="text-muted-foreground">{playbook}</span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

// Loading Skeleton
const DashboardSkeleton = () => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {[1, 2, 3].map((i) => (
        <Card key={i} className="bg-card border-border">
          <CardContent className="p-6">
            <Skeleton className="h-32 w-full" />
          </CardContent>
        </Card>
      ))}
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="bg-card border-border">
        <CardContent className="p-6">
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
      <Card className="bg-card border-border">
        <CardContent className="p-6">
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
    </div>
  </div>
);

export default function PromiseHealthPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [windowDays, setWindowDays] = useState('7');
  const [brandId, setBrandId] = useState(DEFAULT_BRAND_ID);
  
  // Data states
  const [digest, setDigest] = useState(null);
  const [drivers, setDrivers] = useState(null);
  const [score, setScore] = useState(null);
  const [benchmark, setBenchmark] = useState(null);
  const [impact, setImpact] = useState(null);
  const [trend, setTrend] = useState(null);
  const [error, setError] = useState(null);
  
  // Editable assumptions state for Impact tab
  const [assumptionsOpen, setAssumptionsOpen] = useState(false);
  const [editableAssumptions, setEditableAssumptions] = useState({
    gross_margin_pct: 55,
    support_cost_per_ticket: 6,
    expected_reduction_pct: 25
  });
  const [customImpact, setCustomImpact] = useState(null);
  
  // Share Report Modal state
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [slackConnected, setSlackConnected] = useState(false);
  
  // Policy Quick Start state
  const [policyQuickStartOpen, setPolicyQuickStartOpen] = useState(false);
  const [policyStatus, setPolicyStatus] = useState(null);
  
  // Check Slack connection status
  useEffect(() => {
    const checkSlackStatus = async () => {
      try {
        const res = await apiClient.get('/api/integration/status', {
          withCredentials: true
        });
        setSlackConnected(res.data?.slack?.connected === true);
      } catch (err) {
      }
    };
    checkSlackStatus();
  }, []);
  
  // Fetch policy status
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const st = await getPoliciesStatus(brandId);
        if (!cancelled) setPolicyStatus(st);
      } catch (err) {
        if (!cancelled) setPolicyStatus(null);
      }
    })();
    return () => { cancelled = true; };
  }, [brandId]);
  
  // Refresh policy status after Quick Start completes
  const refreshPolicyStatus = async () => {
    try {
      const st = await getPoliciesStatus(brandId);
      setPolicyStatus(st);
    } catch (err) {
    }
  };
  
  // Handle Send to Slack
  const handleSendSlack = async (text) => {
    const token = sessionStorage.getItem('session_token');
    const res = await apiClient.post('/api/v1/integrations/slack/send-report', 
      { text },
      {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      }
    );
    return res.data;
  };
  
  // Build snapshot for share modal
  const buildShareSnapshot = () => {
    const topDriver = drivers?.drivers?.[0] || null;
    const delta = digest?.promise_integrity?.wow_score_delta_vs_14day || 0;
    return {
      windowDays: parseInt(windowDays),
      score: score?.score || digest?.promise_integrity?.score || null,
      deltaText: delta > 0 ? `+${delta}` : delta?.toString(),
      topDriver: topDriver?.name || 'Unknown',
      topDriverRate: topDriver?.rate_per_100 || 0,
      topDriverImpact: topDriver?.weighted_impact || 0,
      benchmarkText: benchmark?.position_text || 'Benchmark pending',
      monthlyLoss: impact?.monthly_margin_loss_estimate || impact?.monthly_loss_estimate || 0,
      potentialSavings: impact?.potential_margin_savings || impact?.potential_savings || 0,
      expectedReduction: editableAssumptions.expected_reduction_pct,
      playbooks: digest?.playbooks?.slice(0, 3).map(p => p.name || p.title || p) || []
    };
  };
  
  // Fetch data
  const fetchData = useCallback(async () => {
    setError(null);
    try {
      const [digestRes, driversRes, scoreRes, benchmarkRes, impactRes, trendRes] = await Promise.all([
        apiClient.get(`/api/v1/decision-graph/digest/weekly?brand_id=${brandId}`),
        apiClient.get(`/api/v1/decision-graph/analytics/promise-break-drivers?brand_id=${brandId}&window_days=${windowDays}&limit=10`),
        apiClient.get(`/api/v1/decision-graph/scores/promise-integrity?brand_id=${brandId}&window_days=${windowDays}`),
        apiClient.get(`/api/v1/decision-graph/benchmark/promise-integrity?brand_id=${brandId}&window_days=${windowDays}`).catch(() => ({ data: null })),
        apiClient.get(`/api/v1/decision-graph/impact/revenue?brand_id=${brandId}&window_days=30`).catch(() => ({ data: null })),
        apiClient.get(`/api/v1/decision-graph/analytics/disappointment-trend?brand_id=${brandId}&window_days=${windowDays}&bucket_days=7`).catch(() => ({ data: null }))
      ]);
      
      setDigest(digestRes.data);
      setDrivers(driversRes.data);
      setScore(scoreRes.data);
      setBenchmark(benchmarkRes.data);
      setImpact(impactRes.data);
      setTrend(trendRes.data);
    } catch (err) {
      console.error('Failed to fetch Promise Health data:', err);
      setError('Failed to load Promise Health data. Please try again.');
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [brandId, windowDays]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };
  
  const handleWindowChange = (value) => {
    setWindowDays(value);
    setLoading(true);
  };
  
  // Update editable assumptions when impact data loads
  useEffect(() => {
    if (impact?.assumptions) {
      setEditableAssumptions({
        gross_margin_pct: Math.round((impact.assumptions.gross_margin_pct || 0.55) * 100),
        support_cost_per_ticket: impact.assumptions.support_cost_per_ticket || 6,
        expected_reduction_pct: Math.round((impact.assumptions.expected_reduction_pct || 0.25) * 100)
      });
    }
  }, [impact]);
  
  // Recalculate impact with custom assumptions
  const recalculateImpact = async () => {
    try {
      const res = await apiClient.get(
        `/api/v1/decision-graph/impact/revenue?brand_id=${brandId}&window_days=30` +
        `&gross_margin_pct=${editableAssumptions.gross_margin_pct / 100}` +
        `&support_cost_per_ticket=${editableAssumptions.support_cost_per_ticket}` +
        `&expected_reduction_pct=${editableAssumptions.expected_reduction_pct / 100}`
      );
      setCustomImpact(res.data);
      toast.success('Impact recalculated with custom assumptions');
    } catch (err) {
      toast.error('Failed to recalculate impact');
    }
  };
  
  // Use custom impact if available, otherwise use original
  const displayImpact = customImpact || impact;
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <MerchantHeader />
        <main className="container mx-auto px-4 py-8 max-w-7xl">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Promise Health</h1>
              <p className="text-muted-foreground mt-1">Loading dashboard...</p>
            </div>
          </div>
          <DashboardSkeleton />
        </main>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <MerchantHeader />
        <main className="container mx-auto px-4 py-8 max-w-7xl">
          <Card className="bg-card border-destructive/50">
            <CardContent className="p-8 text-center">
              <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Unable to Load Data</h2>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button onClick={handleRefresh} variant="outline">
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }
  
  const currentScore = score?.score || digest?.promise_integrity?.score || 0;
  const totalPromises = drivers?.promises || digest?.promise_integrity?.promises || 0;
  const wowDelta = digest?.promise_integrity?.wow_score_delta_vs_14day || 0;
  
  return (
    <div className="min-h-screen bg-background" data-testid="promise-health-dashboard">
      <MerchantHeader />
      
      <main className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
              <ShieldCheck className="h-8 w-8 text-primary" />
              Promise Health
            </h1>
            <p className="text-muted-foreground mt-1">
              Monitor and improve promise delivery across your operations
            </p>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            {/* Policy Status Widget + Quick Start */}
            <div className="flex items-center gap-2">
              <PolicyStatusWidget brandId={brandId} />
              
              {/* Quick Start button only if missing or partially configured */}
              {policyStatus && policyStatus.active_count < policyStatus.total_supported && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPolicyQuickStartOpen(true)}
                  className="text-xs bg-sky-500/10 border-sky-400/30 hover:bg-sky-500/15 hover:border-sky-400/40"
                  title="Configure minimum policies so Decision Pack + Slack approvals are policy-aware"
                  data-testid="policy-quick-start-btn"
                >
                  Policy Quick Start (2 min)
                </Button>
              )}
            </div>
            
            {/* Slack Approval Status Widget */}
            <SlackApprovalStatus brandId={brandId} />
            
            {/* Share Report Button */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="outline" 
                    onClick={() => setShareModalOpen(true)}
                    className="gap-2"
                    data-testid="share-report-btn"
                  >
                    <Share2 className="h-4 w-4" />
                    Share Report
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <div>
                    <p className="font-semibold">{Tooltips.shareReport.title}</p>
                    <p className="text-sm mt-1">{Tooltips.shareReport.body}</p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <Select value={windowDays} onValueChange={handleWindowChange}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Window" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="14">Last 14 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
              </SelectContent>
            </Select>
            
            <Button 
              variant="outline" 
              size="icon"
              onClick={handleRefresh}
              disabled={refreshing}
              data-testid="refresh-button"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
        
        {/* Score Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Main Score Card */}
          <Card className={`md:col-span-1 ${getScoreBgColor(currentScore)} border`}>
            <CardContent className="p-6 flex flex-col items-center justify-center">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <p className="text-sm text-muted-foreground mb-4 cursor-help">Promise Integrity Score</p>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <div>
                      <p className="font-semibold">{Tooltips.promiseIntegrityScore.title}</p>
                      <p className="text-sm mt-1">{Tooltips.promiseIntegrityScore.body}</p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <ScoreRing score={currentScore} />
              <div className="flex items-center gap-2 mt-4">
                {wowDelta >= 0 ? (
                  <TrendingUp className="h-4 w-4 text-emerald-400" />
                ) : (
                  <TrendingDown className="h-4 w-4 text-red-400" />
                )}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className={`text-sm font-medium cursor-help ${wowDelta >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {wowDelta >= 0 ? '+' : ''}{wowDelta} vs last 14d
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <div>
                        <p className="font-semibold">{Tooltips.scoreDelta.title}</p>
                        <p className="text-sm mt-1">{Tooltips.scoreDelta.body}</p>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </CardContent>
          </Card>
          
          {/* Key Metrics */}
          <Card className="md:col-span-2 bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Key Metrics</CardTitle>
              <CardDescription>Performance indicators for {windowDays}-day window</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="p-4 rounded-lg bg-muted/30 cursor-help" data-testid="metric-promises">
                        <p className="text-xs text-muted-foreground mb-1">Total Promises</p>
                        <p className="text-2xl font-bold text-foreground">{totalPromises}</p>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <div>
                        <p className="font-semibold">{Tooltips.totalPromises.title}</p>
                        <p className="text-sm mt-1">{Tooltips.totalPromises.body}</p>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                  
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="p-4 rounded-lg bg-muted/30 cursor-help" data-testid="metric-broken-rate">
                        <p className="text-xs text-muted-foreground mb-1">Broken Rate</p>
                        <p className="text-2xl font-bold text-foreground">
                          {((score?.broken_rate || 0) * 100).toFixed(1)}%
                        </p>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <div>
                        <p className="font-semibold">{Tooltips.brokenRate.title}</p>
                        <p className="text-sm mt-1">{Tooltips.brokenRate.body}</p>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                  
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="p-4 rounded-lg bg-muted/30 cursor-help" data-testid="metric-change-rate">
                        <p className="text-xs text-muted-foreground mb-1">Change Rate</p>
                        <p className="text-2xl font-bold text-foreground">
                          {((score?.change_rate || 0) * 100).toFixed(1)}%
                        </p>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <div>
                        <p className="font-semibold">{Tooltips.changeRate.title}</p>
                        <p className="text-sm mt-1">{Tooltips.changeRate.body}</p>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                  
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="p-4 rounded-lg bg-muted/30 cursor-help" data-testid="metric-urgent-miss">
                        <p className="text-xs text-muted-foreground mb-1">Urgent Miss Rate</p>
                        <p className="text-2xl font-bold text-foreground">
                          {((score?.urgent_miss_rate || 0) * 100).toFixed(1)}%
                        </p>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <div>
                        <p className="font-semibold">{Tooltips.urgentMissRate.title}</p>
                        <p className="text-sm mt-1">{Tooltips.urgentMissRate.body}</p>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Tabs for Drivers and Playbooks */}
        <Tabs defaultValue="drivers" className="space-y-6">
          <TabsList className="bg-muted">
            <TabsTrigger value="drivers" data-testid="tab-drivers">
              <Target className="h-4 w-4 mr-2" />
              Top Drivers
            </TabsTrigger>
            <TabsTrigger value="playbooks" data-testid="tab-playbooks">
              <Lightbulb className="h-4 w-4 mr-2" />
              Playbooks
            </TabsTrigger>
            <TabsTrigger value="trends" data-testid="tab-trends">
              <BarChart3 className="h-4 w-4 mr-2" />
              Trends
            </TabsTrigger>
            <TabsTrigger value="benchmark" data-testid="tab-benchmark">
              <Users className="h-4 w-4 mr-2" />
              Benchmark
            </TabsTrigger>
            <TabsTrigger value="impact" data-testid="tab-impact">
              <DollarSign className="h-4 w-4 mr-2" />
              Impact
            </TabsTrigger>
          </TabsList>
          
          {/* Drivers Tab */}
          <TabsContent value="drivers" className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold">What is Breaking?</h2>
                <p className="text-sm text-muted-foreground">
                  Top disappointment types impacting your promise integrity
                </p>
              </div>
            </div>
            
            {drivers?.drivers?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="drivers-grid">
                {drivers.drivers.map((driver, idx) => (
                  <DriverCard 
                    key={driver.type || idx} 
                    driver={driver} 
                    totalPromises={totalPromises}
                  />
                ))}
              </div>
            ) : (
              <Card className="bg-card border-border border-dashed">
                <CardContent className="p-8 text-center">
                  <ShieldCheck className="h-12 w-12 text-primary mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Promise Breaks Detected</h3>
                  <p className="text-muted-foreground">
                    Great job! No disappointment events found in this time period.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          
          {/* Playbooks Tab */}
          <TabsContent value="playbooks" className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold">What Do I Do Next?</h2>
                <p className="text-sm text-muted-foreground">
                  Actionable recommendations based on your top drivers
                </p>
              </div>
            </div>
            
            {digest?.recommended_playbooks?.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="playbooks-grid">
                {digest.recommended_playbooks.map((rec, idx) => (
                  <PlaybookCard key={rec.driver || idx} recommendation={rec} />
                ))}
              </div>
            ) : (
              <Card className="bg-card border-border border-dashed">
                <CardContent className="p-8 text-center">
                  <Lightbulb className="h-12 w-12 text-primary mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Recommendations</h3>
                  <p className="text-muted-foreground">
                    No specific playbooks needed - your promise health looks good!
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          
          {/* Trends Tab */}
          <TabsContent value="trends" className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold">Disappointment Trends</h2>
              <p className="text-sm text-muted-foreground">
                Track promise breaks over time by severity
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Trend Chart */}
              <Card className="bg-card border-border">
                <CardHeader>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <CardTitle className="text-lg cursor-help">Events Over Time</CardTitle>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <div>
                          <p className="font-semibold">{Tooltips.trendsEventsOverTime.title}</p>
                          <p className="text-sm mt-1">{Tooltips.trendsEventsOverTime.body}</p>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <CardDescription>Disappointment events by severity</CardDescription>
                </CardHeader>
                <CardContent>
                  {trend?.trend?.length > 0 ? (
                    <DisappointmentTrendChart data={trend.trend} height={250} />
                  ) : (
                    <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                      No trend data available
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Driver Distribution */}
              <Card className="bg-card border-border">
                <CardHeader>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <CardTitle className="text-lg cursor-help">Driver Distribution</CardTitle>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <div>
                          <p className="font-semibold">{Tooltips.trendsDriverDistribution.title}</p>
                          <p className="text-sm mt-1">{Tooltips.trendsDriverDistribution.body}</p>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <CardDescription>Top drivers by event count</CardDescription>
                </CardHeader>
                <CardContent>
                  {drivers?.drivers?.length > 0 ? (
                    <DriverDistributionChart data={drivers.drivers.slice(0, 5)} height={250} />
                  ) : (
                    <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                      No driver data available
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          {/* Benchmark Tab */}
          <TabsContent value="benchmark" className="space-y-6">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-xl font-semibold">How Do You Compare?</h2>
                <p className="text-sm text-muted-foreground">
                  Compare your promise health to similar merchants
                </p>
              </div>
              {/* CTA for Benchmark tab */}
              <Button variant="outline" asChild>
                <Link to="/dashboard/analytics">
                  <BarChart3 className="h-4 w-4 mr-2" />
                  View Full Analytics
                </Link>
              </Button>
            </div>
            
            {benchmark && benchmark.position && benchmark.position !== 'unknown' ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Position Card */}
                <Card className={`bg-card border-border ${
                  benchmark.position === 'top_quartile' ? 'border-emerald-500/50' :
                  benchmark.position === 'above_median' ? 'border-green-500/50' :
                  benchmark.position === 'below_median' ? 'border-yellow-500/50' :
                  benchmark.position === 'bottom_quartile' ? 'border-red-500/50' : ''
                }`}>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Users className="h-5 w-5 text-primary" />
                      Your Position
                    </CardTitle>
                    {benchmark.cohort_key && (
                      <CardDescription>
                        Cohort: {benchmark.cohort_key.replace(/\|/g, ' • ')}
                      </CardDescription>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="text-center mb-4">
                      <Badge className={`text-lg px-4 py-2 ${
                        benchmark.position === 'top_quartile' ? 'bg-emerald-500/20 text-emerald-400' :
                        benchmark.position === 'above_median' ? 'bg-green-500/20 text-green-400' :
                        benchmark.position === 'below_median' ? 'bg-yellow-500/20 text-yellow-400' :
                        benchmark.position === 'bottom_quartile' ? 'bg-red-500/20 text-red-400' :
                        'bg-gray-500/20 text-gray-400'
                      }`}>
                        {benchmark.position?.replace(/_/g, ' ').toUpperCase()}
                      </Badge>
                    </div>
                    {benchmark.position_message && (
                      <p className="text-center text-sm text-muted-foreground">
                        {benchmark.position_message}
                      </p>
                    )}
                  </CardContent>
                </Card>
                
                {/* Comparison Chart */}
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-lg">Score Comparison</CardTitle>
                    <CardDescription>
                      Your score vs cohort percentiles
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {benchmark.benchmark?.promise_integrity_score ? (
                      <BenchmarkComparisonChart 
                        brandScore={benchmark.brand?.score} 
                        benchmark={benchmark.benchmark.promise_integrity_score}
                        height={200}
                      />
                    ) : (
                      <div className="h-[200px] flex items-center justify-center text-muted-foreground text-center">
                        <div>
                          <p>Benchmark data not available.</p>
                          <p className="text-xs mt-1">Run daily rollup to compute cohort benchmarks.</p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : (
              /* Improved "Not Available" state */
              <Card className="bg-card border-border">
                <CardContent className="p-8">
                  <div className="flex flex-col md:flex-row items-center gap-8">
                    <div className="flex-shrink-0">
                      <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
                        <Rocket className="h-12 w-12 text-primary" />
                      </div>
                    </div>
                    <div className="text-center md:text-left">
                      <h3 className="text-xl font-semibold mb-2">Benchmark Not Yet Available</h3>
                      <p className="text-muted-foreground mb-4 max-w-lg">
                        We publish benchmarks only when <strong>30+ similar merchants</strong> exist in your cohort. 
                        This protects data privacy while ensuring meaningful comparisons.
                      </p>
                      <div className="flex flex-wrap gap-4 items-center justify-center md:justify-start">
                        <Badge variant="outline" className="text-sm py-1.5 px-3">
                          <Clock className="h-3 w-3 mr-1.5" />
                          Cohort building in progress
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          You are building an edge before most merchants show up
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          
          {/* Impact Tab */}
          <TabsContent value="impact" className="space-y-6">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-xl font-semibold">What is This Costing You?</h2>
                <p className="text-sm text-muted-foreground">
                  Estimated revenue impact from promise breaks (30-day window)
                </p>
              </div>
              {/* CTA for Impact tab */}
              <div className="flex gap-2">
                <Button variant="outline" asChild>
                  <Link to="/dashboard/exception-radar">
                    <Search className="h-4 w-4 mr-2" />
                    Investigate
                  </Link>
                </Button>
              </div>
            </div>
            
            {displayImpact && !displayImpact.error ? (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Total Cost Card */}
                <Card className="bg-red-500/10 border-red-500/30">
                  <CardContent className="p-6 text-center">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <p className="text-sm text-muted-foreground mb-2 cursor-help">Estimated Monthly Loss</p>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <div>
                            <p className="font-semibold">{Tooltips.estimatedMonthlyLoss.title}</p>
                            <p className="text-sm mt-1">{Tooltips.estimatedMonthlyLoss.body}</p>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <p className="text-4xl font-bold text-red-400">
                      ${displayImpact.estimated_costs?.total_estimated_cost?.toLocaleString(undefined, {maximumFractionDigits: 0}) || '0'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      ~${displayImpact.estimated_savings?.annual_loss_projection?.toLocaleString(undefined, {maximumFractionDigits: 0}) || '0'}/year
                    </p>
                  </CardContent>
                </Card>
                
                {/* Potential Savings */}
                <Card className="bg-emerald-500/10 border-emerald-500/30">
                  <CardContent className="p-6 text-center">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <p className="text-sm text-muted-foreground mb-2 cursor-help">Potential Savings</p>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <div>
                            <p className="font-semibold">{Tooltips.potentialSavings.title}</p>
                            <p className="text-sm mt-1">{Tooltips.potentialSavings.body}</p>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <p className="text-4xl font-bold text-emerald-400">
                      ${displayImpact.estimated_savings?.if_playbooks_applied?.toLocaleString(undefined, {maximumFractionDigits: 0}) || '0'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      If playbooks reduce breaks by {editableAssumptions.expected_reduction_pct}%
                    </p>
                  </CardContent>
                </Card>
                
                {/* Annual Savings */}
                <Card className="bg-primary/10 border-primary/30">
                  <CardContent className="p-6 text-center">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <p className="text-sm text-muted-foreground mb-2 cursor-help">Annual Savings Potential</p>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <div>
                            <p className="font-semibold">{Tooltips.annualSavingsPotential.title}</p>
                            <p className="text-sm mt-1">{Tooltips.annualSavingsPotential.body}</p>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <p className="text-4xl font-bold text-primary">
                      ${displayImpact.estimated_savings?.annual_savings_projection?.toLocaleString(undefined, {maximumFractionDigits: 0}) || '0'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      AOV: ${displayImpact.assumptions?.aov?.toFixed(0) || '50'}
                    </p>
                  </CardContent>
                </Card>
                
                {/* Cost Breakdown Chart */}
                <Card className="lg:col-span-3 bg-card border-border">
                  <CardHeader>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <CardTitle className="text-lg cursor-help">Cost Breakdown</CardTitle>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <div>
                            <p className="font-semibold">{Tooltips.costBreakdown.title}</p>
                            <p className="text-sm mt-1">{Tooltips.costBreakdown.body}</p>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <CardDescription>
                      Where your money is going
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <RevenueImpactChart data={displayImpact} height={200} />
                  </CardContent>
                </Card>
                
                {/* Editable Assumptions */}
                <Card className="lg:col-span-3 bg-card border-border">
                  <Collapsible open={assumptionsOpen} onOpenChange={setAssumptionsOpen}>
                    <CollapsibleTrigger asChild>
                      <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors rounded-t-lg">
                        <CardTitle className="text-sm flex items-center justify-between">
                          <span className="flex items-center gap-2">
                            <Settings className="h-4 w-4" />
                            Assumptions Used
                          </span>
                          <ChevronDown className={`h-4 w-4 transition-transform ${assumptionsOpen ? 'rotate-180' : ''}`} />
                        </CardTitle>
                      </CardHeader>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                          <div className="space-y-2">
                            <Label htmlFor="gm" className="text-xs">Gross Margin %</Label>
                            <Input
                              id="gm"
                              type="number"
                              min={0}
                              max={100}
                              value={editableAssumptions.gross_margin_pct}
                              onChange={(e) => setEditableAssumptions(prev => ({
                                ...prev,
                                gross_margin_pct: Number(e.target.value)
                              }))}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="support" className="text-xs">Support Cost/Ticket ($)</Label>
                            <Input
                              id="support"
                              type="number"
                              min={0}
                              value={editableAssumptions.support_cost_per_ticket}
                              onChange={(e) => setEditableAssumptions(prev => ({
                                ...prev,
                                support_cost_per_ticket: Number(e.target.value)
                              }))}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="reduction" className="text-xs">Expected Reduction %</Label>
                            <Input
                              id="reduction"
                              type="number"
                              min={0}
                              max={100}
                              value={editableAssumptions.expected_reduction_pct}
                              onChange={(e) => setEditableAssumptions(prev => ({
                                ...prev,
                                expected_reduction_pct: Number(e.target.value)
                              }))}
                            />
                          </div>
                          <Button onClick={recalculateImpact}>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Recalculate
                          </Button>
                        </div>
                        {customImpact && (
                          <p className="text-xs text-emerald-400 mt-3">
                            ✓ Using custom assumptions
                          </p>
                        )}
                      </CardContent>
                    </CollapsibleContent>
                  </Collapsible>
                </Card>
              </div>
            ) : (
              <Card className="bg-card border-border border-dashed">
                <CardContent className="p-8 text-center">
                  <DollarSign className="h-12 w-12 text-primary mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">Impact Data Unavailable</h3>
                  <p className="text-muted-foreground">
                    Need more promise data to calculate revenue impact.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
        
        {/* Severity Summary */}
        {digest?.severity_summary && digest.severity_summary.total_events > 0 && (
          <Card className="mt-8 bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg">Severity Distribution (7d)</CardTitle>
              <CardDescription>
                Breakdown of {digest.severity_summary.total_events} disappointment events by severity
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <div className="flex h-4 rounded-full overflow-hidden bg-muted">
                    {digest.severity_summary.high > 0 && (
                      <div 
                        className="bg-red-500 transition-all duration-500"
                        style={{ width: `${(digest.severity_summary.high / digest.severity_summary.total_events) * 100}%` }}
                      />
                    )}
                    {digest.severity_summary.med > 0 && (
                      <div 
                        className="bg-yellow-500 transition-all duration-500"
                        style={{ width: `${(digest.severity_summary.med / digest.severity_summary.total_events) * 100}%` }}
                      />
                    )}
                    {digest.severity_summary.low > 0 && (
                      <div 
                        className="bg-green-500 transition-all duration-500"
                        style={{ width: `${(digest.severity_summary.low / digest.severity_summary.total_events) * 100}%` }}
                      />
                    )}
                  </div>
                </div>
                <div className="flex gap-4 text-sm">
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full bg-red-500"></span>
                    High: {digest.severity_summary.high}
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                    Med: {digest.severity_summary.med}
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full bg-green-500"></span>
                    Low: {digest.severity_summary.low}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Footer Info */}
        <div className="mt-8 text-center text-sm text-muted-foreground">
          <p>
            Last updated: {digest?.generated_at ? new Date(digest.generated_at).toLocaleString() : 'N/A'}
          </p>
        </div>
      </main>
      
      {/* Share Report Modal */}
      <ShareReportModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        snapshot={buildShareSnapshot()}
        shareUrl={`${window.location.origin}/dashboard/promise-health`}
        onSendSlack={handleSendSlack}
        slackConnected={slackConnected}
      />
      
      {/* Policy Quick Start Modal */}
      <PoliciesQuickStartModal
        open={policyQuickStartOpen}
        onClose={() => setPolicyQuickStartOpen(false)}
        brandId={brandId}
        onDone={async () => {
          await refreshPolicyStatus();
        }}
      />
    </div>
  );
}
